import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { subscribeQuizClient, quizAnswer } from "./quizApiService";
import { getStudentSession, normalizeCard } from "./quizSession";

const OPTION_COLORS = ["#EF4444", "#3B82F6", "#F59E0B", "#22C55E", "#8B5CF6", "#EC4899"];
const OPTION_LABELS = ["A", "B", "C", "D", "E", "F"];

function shuffle(array) {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export default function QuizStudentPlay() {
  const { t } = useTranslation();
  const { sessionId } = useParams(); // shortId
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const student = useRef(getStudentSession(sessionId)).current;
  const studentName = student?.name || searchParams.get("name") || "—";

  const [question, setQuestion] = useState(null);
  const [secondsPerAnswer, setSecondsPerAnswer] = useState(30);
  const [timeLeft, setTimeLeft] = useState(30);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);

  const [selectedOption, setSelectedOption] = useState(null);
  const [textAnswer, setTextAnswer] = useState("");
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);

  const currentCardIdRef = useRef(null);
  const questionStartRef = useRef(0);
  const scoreRef = useRef(0);
  const totalRef = useRef(0);
  const navigatedRef = useRef(false);

  const goToResults = useCallback(() => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    navigate(
      `/quiz/session/${sessionId}/results?name=${encodeURIComponent(studentName)}&score=${scoreRef.current}&total=${totalRef.current}`
    );
  }, [navigate, sessionId, studentName]);

  // Client event stream.
  useEffect(() => {
    if (!student?.uuidParticipant) {
      navigate(`/quiz/join/${sessionId}`, { replace: true });
      return undefined;
    }

    const unsubscribe = subscribeQuizClient(sessionId, student.uuidParticipant, {
      onEvent: (event) => {
        if (event.eventType !== "NEXT_QUESTION_EVENT") return;
        const payload = event.payload || {};
        const card = payload.card;

        // Quiz finished.
        if (!card) {
          goToResults();
          return;
        }

        // Ignore replays of the question we already display.
        if (card.cardId === currentCardIdRef.current) return;

        currentCardIdRef.current = card.cardId;
        const normalized = normalizeCard(card);
        if (normalized?.isChoice && normalized.options.length > 1) {
          normalized.options = shuffle(normalized.options);
        }
        const secs = payload.secondsPerAnswer || 30;

        setQuestion(normalized);
        setSecondsPerAnswer(secs);
        setTimeLeft(secs);
        setSelectedOption(null);
        setTextAnswer("");
        setAnswered(false);
        questionStartRef.current = Date.now();

        setQuestionNumber((prev) => {
          const num = prev + 1;
          const total = num + (payload.questionsLeft || 0);
          totalRef.current = Math.max(totalRef.current, total);
          setTotalQuestions(totalRef.current);
          return num;
        });
      },
    });

    return unsubscribe;
  }, [sessionId, student, navigate, goToResults]);

  // Countdown timer for the current question.
  useEffect(() => {
    if (!question || answered || timeLeft <= 0) return undefined;
    const timer = setInterval(() => setTimeLeft((prev) => Math.max(prev - 1, 0)), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, answered, question]);

  const submitAnswer = useCallback(
    async (answerText, isCorrect) => {
      if (answered || !question) return;
      setAnswered(true);
      if (isCorrect) {
        scoreRef.current += 1;
        setScore(scoreRef.current);
      }
      const timeTakenMs = Math.max(Date.now() - questionStartRef.current, 0);
      try {
        await quizAnswer({
          quizShortId: sessionId,
          uuidParticipant: student.uuidParticipant,
          cardId: question.cardId,
          answerText,
          timeTakenMs,
        });
      } catch (err) {
        console.error("Failed to submit answer:", err);
      }
    },
    [answered, question, sessionId, student]
  );

  // Memoized so timer ticks (which re-render every second) never recreate the
  // question DOM node — this prevents the text/image from flickering.
  const questionContent = useMemo(() => {
    if (!question) return null;
    return (
      <div className="px-5 pb-2 max-w-2xl mx-auto w-full">
        <div className="bg-white rounded-[32px] border border-blue-100 shadow-sm p-8 min-h-[160px] flex flex-col items-center justify-center text-center gap-4">
          <h2
            className="text-2xl md:text-3xl font-bold text-gray-800 leading-snug"
            dangerouslySetInnerHTML={{ __html: question.questionText }}
          />
          {question.questionImage && (
            <img src={question.questionImage} alt="" className="max-h-52 rounded-2xl object-contain" />
          )}
        </div>
      </div>
    );
  }, [question]);

  const handleSelectOption = (idx) => {
    if (answered || !question) return;
    setSelectedOption(idx);
    const option = question.options[idx];
    submitAnswer(option.text, !!option.correct);
  };

  const handleSubmitText = (e) => {
    e.preventDefault();
    if (answered || !question || !textAnswer.trim()) return;
    submitAnswer(textAnswer.trim(), null);
  };

  // No question yet -> "get ready" waiting screen (kept on the same SSE
  // subscription so the first question is never missed during a screen handoff).
  if (!question) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-5 max-w-[1500px] mx-auto w-full">
        <div className="w-full max-w-md">
          <img
            src="/assets/images/LOGO.png"
            alt="Zubrus"
            className="mx-auto mb-10 h-12 object-contain"
          />

          <div className="bg-white rounded-[32px] p-8 text-center shadow-sm border border-blue-100">
            <div className="text-6xl mb-6 animate-bounce">⏳</div>

            <h2 className="text-2xl font-bold text-darkblue mb-2">
              {t("quiz.getReady")}, {studentName}!
            </h2>

            <p className="text-gray-500 mb-8">{t("quiz.waitingForTeacher")}</p>

            <div className="flex justify-center mb-6">
              <svg
                className="animate-spin h-12 w-12 text-blue-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            </div>

            <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full">
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
              <p className="text-sm font-medium text-blue-700">{t("quiz.waitingToStart")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const radius = 22;
  const circumference = 2 * Math.PI * radius;
  const timerPercentage = (timeLeft / secondsPerAnswer) * 100;
  const strokeDashoffset = circumference - (timerPercentage / 100) * circumference;
  const timeUp = timeLeft <= 0 && !answered;

  const getOptionState = (idx) => {
    if (!answered) return "default";
    const option = question.options[idx];
    if (option.correct) return "correct";
    if (selectedOption === idx && !option.correct) return "wrong";
    return "dimmed";
  };

  return (
    <div className="flex-1 w-full flex flex-col font-sans">
      {/* Top Bar */}
      <div className="px-5 py-4 w-full max-w-2xl mx-auto flex items-center justify-between">
        <div className="bg-white rounded-full px-4 py-2 shadow-sm border border-blue-100 font-bold text-darkblue">
          {questionNumber}
          {totalQuestions ? (
            <span className="text-gray-400 font-medium"> / {totalQuestions}</span>
          ) : null}
        </div>

        {/* Circular Timer */}
        <div className="relative flex items-center justify-center w-14 h-14 bg-white rounded-full shadow-sm border border-blue-100">
          <svg className="w-12 h-12 transform -rotate-90">
            <circle cx="24" cy="24" r={radius} stroke="currentColor" strokeWidth="4" fill="transparent" className="text-gray-100" />
            <circle
              cx="24"
              cy="24"
              r={radius}
              stroke="currentColor"
              strokeWidth="4"
              fill="transparent"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className={`transition-all duration-1000 ease-linear ${timeLeft <= 5 ? "text-red-500" : "text-blue-500"}`}
            />
          </svg>
          <div className={`absolute inset-0 flex items-center justify-center font-bold text-lg ${timeLeft <= 5 ? "text-red-500 animate-pulse" : "text-darkblue"}`}>
            {timeLeft}
          </div>
        </div>

        <div className="bg-white rounded-full px-4 py-2 shadow-sm border border-blue-100 font-bold text-darkblue">
          🌟 {score}
        </div>
      </div>

      {/* Question */}
      {questionContent}

      {/* Answer area */}
      {question.isChoice && question.options.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 max-w-2xl mx-auto w-full flex-1 mb-8">
          {question.options.map((option, idx) => {
            const state = getOptionState(idx);
            const baseColor = OPTION_COLORS[idx % OPTION_COLORS.length];

            let extraClass = "hover:-translate-y-1 hover:shadow-lg active:scale-95 cursor-pointer shadow-md";
            let ringClass = "";
            let opacityClass = "";

            if (answered) {
              extraClass = "cursor-default transition-all duration-500";
              if (state === "correct") ringClass = "ring-8 ring-white/50 scale-105 z-10 shadow-xl";
              if (state === "wrong") {
                ringClass = "ring-4 ring-red-200";
                opacityClass = "opacity-70";
              }
              if (state === "dimmed") opacityClass = "opacity-40 scale-95";
            }

            return (
              <button
                key={idx}
                onClick={() => handleSelectOption(idx)}
                disabled={answered || timeUp}
                className={`rounded-[24px] p-6 text-white font-bold text-xl md:text-2xl text-left flex items-center gap-4 transition-all duration-300 ${extraClass} ${ringClass} ${opacityClass} ${
                  timeUp ? "opacity-50 cursor-not-allowed" : ""
                }`}
                style={{ backgroundColor: baseColor }}
              >
                <span className="w-12 h-12 rounded-full bg-black/20 flex items-center justify-center text-lg font-bold shrink-0 shadow-inner">
                  {OPTION_LABELS[idx % OPTION_LABELS.length]}
                </span>
                <span className="flex-1 drop-shadow-sm">{option.text}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="p-5 max-w-2xl mx-auto w-full flex-1 mb-8">
          {!answered ? (
            <form onSubmit={handleSubmitText} className="flex flex-col gap-4">
              <input
                type="text"
                value={textAnswer}
                onChange={(e) => setTextAnswer(e.target.value)}
                placeholder={t("quiz.typeYourAnswer") || "Type your answer..."}
                disabled={timeUp}
                autoFocus
                className="w-full h-[64px] bg-white border-2 border-blue-100 rounded-[20px] px-6 text-lg font-medium text-center focus:border-blue-400 outline-none transition-all placeholder:text-gray-400 text-black"
              />
              <button
                type="submit"
                disabled={!textAnswer.trim() || timeUp}
                className={`primary-btn w-full !rounded-full h-12 !py-0 flex items-center justify-center text-base font-semibold ${
                  !textAnswer.trim() || timeUp ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {t("quiz.submitAnswer") || "Submit answer"}
              </button>
            </form>
          ) : (
            <div className="bg-white rounded-[24px] border border-blue-100 shadow-sm p-6 text-center">
              <p className="text-lg font-semibold text-darkblue">
                {t("quiz.answerSubmitted") || "Answer submitted!"}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Status banner */}
      {(answered || timeUp) && (
        <div className="px-5 pb-8 max-w-2xl mx-auto w-full text-center">
          {timeUp ? (
            <p className="text-base font-semibold text-red-500">
              {t("quiz.timesUp") || "Time's up!"}
            </p>
          ) : (
            <p className="text-base font-medium text-gray-500">
              {t("quiz.waitingForNext") || "Waiting for the next question..."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
